import _ from 'lodash';
import {
    getValidationSchemaOfEffect,
    getOptionsDefaultsOfEffect,
    getOptionsMappingsOfEffect,
    getPresetNameOfEffect
} from './effectSchemaAPI';
import {validateByType, isValueInRange, TYPES} from '../validation';
import reporter from '../../reporter';
import {generateError} from '../errors';
import {capitalize} from '../../utils';

const effectOptionsForEffectInfoLink = (effectName) => `https://www.wix.com/code/reference/$w.EffectOptions.html#${capitalize(effectName)}EffectOptions`;
const effectInfoLink = (functionName) => `https://www.wix.com/code/reference/$w.HiddenMixin.html#${functionName}`;
const effectOptionsInfoLink = 'https://www.wix.com/code/reference/$w.EffectOptions.html#';

function validateAndReportDeprecatedEffect(effectName, options, functionName, compName) {
    if (!_.isEmpty(options)) {
        reporter.reportSdkWarning(generateError.warning_deprecated_effect_with_options({
            functionName,
            compName,
            effectName,
            infoLink: effectInfoLink(functionName)
        }));
        return false;
    }
    // TODO Uncomment when new docs are released
    // reporter.reportSdkWarning(generateError.warning_deprecated_effect_name({
    //     functionName,
    //     compName,
    //     effectName,
    //     infoLink: effectInfoLink(functionName)
    // }));
    return true;
}

function reportInvalidOptionsValue(value, key, functionName, compName, effectName) {
    reporter.reportSdkWarning(generateError.warning_invalid_effect_options({
        functionName,
        compName,
        effectName,
        wrongProperty: 'value',
        wrongValue: `the key "${key}" cannot be set to the value "${value}"`,
        infoLink: effectOptionsForEffectInfoLink(effectName)
    }));
}

function validateOptionKeysAndReportIfNecessary(effectName, optionKeys, validKeys, functionName, compName) {
    const invalidKeys = _.difference(optionKeys, validKeys);

    if (!_.isEmpty(invalidKeys)) {
        reporter.reportSdkWarning(generateError.warning_invalid_effect_options({
            functionName,
            compName,
            effectName,
            wrongProperty: 'keys',
            wrongValue: invalidKeys,
            infoLink: effectOptionsForEffectInfoLink(effectName)
        }));
        return false;
    }
    return true;
}

function validateOptionValuesAndReportIfNecessary(effectName, options, validations, functionName, compName) {
    let res = true;
    _.forEach(options, (value, k) => {
        const effectValidations = validations[k];
        if (!validateByType({value, expectedType: effectValidations.type})) {
            reportInvalidOptionsValue(value, k, functionName, compName, effectName);
            res = false;
            return;
        }

        if (effectValidations.enum && !_.includes(effectValidations.enum, value)) {
            reportInvalidOptionsValue(value, k, functionName, compName, effectName);
            res = false;
            return;
        }

        if (effectValidations.range && !isValueInRange({value, minValue: effectValidations.range.minValue, maxValue: effectValidations.range.maxValue})) {
            reportInvalidOptionsValue(value, k, functionName, compName, effectName);
            res = false;
        }
    });
    return res;
}

function validateEffect(effect, options, functionName, compName, deprecatedValues) {
    if (!effect) {
        if (!_.isEmpty(options)) {
            reporter.reportSdkWarning(generateError.warning_effect_options_not_set({
                functionName,
                compName,
                infoLink: effectInfoLink(functionName)
            }));
            return false;
        }
        return true;
    }

    if (_.includes(deprecatedValues, effect)) {
        return validateAndReportDeprecatedEffect(effect, options, functionName, compName);
    }

    const validationSchema = getValidationSchemaOfEffect(effect);
    if (!validationSchema) {
        reporter.reportSdkWarning(generateError.warning_invalid_effect_name({
            functionName,
            compName,
            effectName: effect,
            infoLink: effectInfoLink(functionName)
        }));

        return false;
    }

    if (!_.isNil(options) && !validateByType({value: options, expectedType: TYPES.OBJECT})) {
        reporter.reportSdkWarning(generateError.warning_invalid_type_effect_options({
            functionName,
            compName,
            effectName: effect,
            wrongValue: options,
            infoLink: effectOptionsInfoLink
        }));
        return false;
    }

    const validKeys = _.keys(validationSchema);
    const optionKeys = _.keys(options);
    const allOptionKeysValid = validateOptionKeysAndReportIfNecessary(effect, optionKeys, validKeys, functionName, compName);
    if (!allOptionKeysValid) {
        return false;
    }

    return validateOptionValuesAndReportIfNecessary(effect, options, validationSchema, functionName, compName);
}

function buildEffectParams(options, optionsDefaults, optionsMappings) {
    const params = _.clone(optionsDefaults);
    _.forEach(options, (v, k) => {
        const path = _.get(optionsMappings, `keys.${k}`) || k;

        let value = v;
        value = _.get(optionsMappings, `values.${k}.convertMsToSecs`) ? v / 1000 : value;
        value = _.get(optionsMappings, `values.${k}.${v}`, value);

        _.set(params, path, value);
    });
    return params;
}

function buildEffectObject(effect, options, functionData) {
    const {deprecatedValues, suffix, noSuffixPresets} = functionData;
    if (_.includes(deprecatedValues, effect)) {
        return {presetName: effect, params: {}};
    }

    const presetName = getPresetNameOfEffect(effect, suffix, noSuffixPresets);
    const optionsDefaults = getOptionsDefaultsOfEffect(effect);
    const params = buildEffectParams(options, optionsDefaults, getOptionsMappingsOfEffect(effect));

    return {
        presetName,
        params
    };
}

export default {
    validateEffect,
    buildEffectObject
};
